import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AudioDownloadRenderingStatus } from 'services/api/entities/audioDownloadRendering';
import songsApi from 'services/api/songs/songsApi';
import { isDownloadLimitError } from 'services/helpers/error';
import { appendDownloadLink } from 'services/hooks/download/helpers';
import useMessageSnackbar from 'services/hooks/useMessageSnackbar';

interface UseDownloadStems {
  songId: string;
  onSuccess: (status: AudioDownloadRenderingStatus) => void;
}

const useDownloadStems = ({ songId, onSuccess }: UseDownloadStems) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { displayErrorSnackbar } = useMessageSnackbar();

  const [downloadStems, { data, isLoading, error }] =
    songsApi.useDownloadStemsMutation();

  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [isStemRenderingDialogOpen, setIsStemRenderingDialogOpen] =
    useState(false);

  useEffect(() => {
    if (!data) return;

    const { status } = data;

    if (status === AudioDownloadRenderingStatus.Finished) {
      appendDownloadLink(data.downloadUrl);
    } else {
      setIsStemRenderingDialogOpen(true);
    }
    onSuccess(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dispatch]);

  useEffect(() => {
    if (!error) {
      return;
    }

    if (isDownloadLimitError(error)) {
      setIsLimitDialogOpen(true);
      return;
    }

    displayErrorSnackbar(t('messages:error:unknown'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, error]);

  return {
    success: !!data,
    loading: isLoading,
    downloadStems: () => downloadStems({ songId }),
    isLimitDialogOpen,
    closeLimitDialog: () => setIsLimitDialogOpen(false),
    isStemRenderingDialogOpen,
    closeStemRenderingDialog: () => setIsStemRenderingDialogOpen(false),
  };
};

export default useDownloadStems;
